import { Injectable } from '@angular/core';
import { RouteHttpService } from './route.http.service';
import { objectToCamel } from 'ts-case-convert';
import { lastValueFrom } from 'rxjs';
import { GeoJsonObject, IAvailabilities, IApps, IRouteDetails, IRouteDraft, IRouteScheduledUpdate, IRouteSetup, IRouteSetupDetail, ITrip, IRouteAlteration, IProcessingOptions, IRestrictions } from 'src/app/models';
import { ExpeditionStatus, RestrictionsType } from 'src/app/models/enums';
import { UtilsService } from '../utils/utils.service';

@Injectable({
  providedIn: 'root',
})
export class RouteService {
  constructor(private routeHttpService: RouteHttpService, private utilsService: UtilsService) { }

  async getAvailabilities(serviceId: number, page?: number, size?: number, availabilityId?: number) {
    const source$ = this.routeHttpService.getAvailablities(serviceId, page, size, availabilityId);
    const response = await lastValueFrom(source$);
    const availabilities = objectToCamel(response as object);
    const result = this.getData(availabilities);
    return result;
  }
  
  async getDestinationAvailabilityStops(availabilityId: number, inStopId: number, page: number, size: number) {
    const source$ = this.routeHttpService.getAvailablityStops(availabilityId, page, size, inStopId);
    const response = await lastValueFrom(source$);
    const stops = objectToCamel(response as object);
    const stopsArray = this.getData(stops);
    return stopsArray;
  }
  
  async getAvailabilityStops(availabilityId: number, page: number, size: number) {
    const source$ = this.routeHttpService.getAvailablityStops(availabilityId, page, size);
    const response = await lastValueFrom(source$);
    const stops = objectToCamel(response as object);
    const stopsArray = this.getData(stops);
    return stopsArray;
  }

  async getServices(page: number, size: number) {
    const source$ = this.routeHttpService.getServices(page, size);
    const response = await lastValueFrom(source$);
    const services = objectToCamel(response as object);
    const result = this.getData(services);
    return result;
  }

  async getService(idService: number, includeTownStops = false) {
    const source$ = this.routeHttpService.getService(idService, includeTownStops);
    const response = await lastValueFrom(source$);
    const service = objectToCamel(response as object);
    return service;
  }

  async getServiceConfig(idService: number) {
    const source$ = this.routeHttpService.getServiceConfig(idService);
    const response = await lastValueFrom(source$);
    const serviceConfig = objectToCamel(response as object);
    return serviceConfig;
  }
  
  getCurrentDomain(): string {
    return window.location.origin;
  }

  async importServiceFromGtfs(data: any, clientId: number, snapToRoad: boolean, townId?: number, townName?: string, appIds?: number[]) {
    const source$ = this.routeHttpService.importServiceFromGtfs(data, clientId, snapToRoad, townId, townName, appIds);
    const response = await lastValueFrom(source$);
    const services = objectToCamel(response as object);
    const result = this.getData(services);
    return result;
  }

  async importServiceFromExcel(data: any, clientId: number, snapToRoad: boolean, townId?: number, townName?: string) {
    const source$ = this.routeHttpService.importServiceFromExcel(data, clientId, snapToRoad, townId, townName);
    const response = await lastValueFrom(source$);
    return response;
  }

  async downloadGtfs(routeId: number) {
    const source$ = this.routeHttpService.downloadGtfs(routeId);
    const response = await lastValueFrom(source$);
    return response;
  }

  async getRoutesByServiceInfo(serviceIds: number[], page?: number, size?: number) {
    const source$ = this.routeHttpService.getRoutesByServiceInfo(serviceIds, page, size);
    const response = await lastValueFrom(source$);
    const serviceArray = this.getData(response);
    return serviceArray;
  }

  async getRoutesSearch(serviceIds: number[], page?: number, size?: number, searchValue?: string) {
    const source$ = this.routeHttpService.getRoutesSearch(serviceIds, page, size, searchValue);
    const response = await lastValueFrom(source$);
    const serviceArray = this.getData(response);
    return serviceArray;
  }

  async getRouteTerrains(routeId: number) {
    const source$ = this.routeHttpService.getRouteTerrains(routeId);
    const response = await lastValueFrom(source$);
    const terrainsArray = objectToCamel(response as []);
    return terrainsArray;
  }

  async getRelateds(serviceId: number) {
    const source$ = this.routeHttpService.getRelateds(serviceId);
    const response = await lastValueFrom(source$);
    const relatedsArray = objectToCamel(response as []);
    return relatedsArray;
  }

  async updateRoute(route: IRouteDetails, options?: any, townStopsIncluded = false) {
    const source$ = this.routeHttpService.updateRoute(route, options, townStopsIncluded);
    const response = await lastValueFrom(source$);
    const responseRoute = objectToCamel(response as []);
    return responseRoute;
  }

  async getRouteDraftTerrain(routeId: number) {
    const source$ = this.routeHttpService.getRouteDraftTerrain(routeId);
    const response = await lastValueFrom(source$);
    if (!response) return null;
    const terrain = objectToCamel(response as object) as GeoJsonObject;
    if (!terrain.features?.length) return null;
    return { terrain } as IRouteDraft;
  }

  async saveRouteDraftTerrain(routeId: number, draft: IRouteDraft) {
    const source$ = this.routeHttpService.saveRouteDraftTerrain(routeId, draft);
    const response = await lastValueFrom(source$);
    return objectToCamel(response as object) as IRouteDraft;
  }

  async applyRouteDraftTerrain(routeId: number, processingOptions?: IProcessingOptions) {
    const source$ = this.routeHttpService.applyRouteDraftTerrain(routeId, processingOptions);
    const response = await lastValueFrom(source$);
    return response;
  }

  async getRouteScheduledChange(routeId: number) {
    const source$ = this.routeHttpService.getRouteScheduledChange(routeId);
    let response: unknown;
    try {
      response = await lastValueFrom(source$);
    } catch (error: any) {
      if (error?.status === 404) return null;
      throw error;
    }
    if (!response) return null;
    const scheduled = this.utilsService.convertDateTimeArrays(objectToCamel(response as object)) as IRouteScheduledUpdate;
    return scheduled?.dateTime ? scheduled : null;
  }

  async scheduleRouteTerrainUpdate(routeId: number, scheduledUpdate: IRouteScheduledUpdate, processingOptions?: IProcessingOptions) {
    const source$ = this.routeHttpService.scheduleRouteTerrainUpdate(routeId, scheduledUpdate, processingOptions);
    const response = await lastValueFrom(source$);
    return response;
  }

  async deleteRouteScheduledChange(routeId: number) {
    const source$ = this.routeHttpService.deleteRouteScheduledChange(routeId);
    const response = await lastValueFrom(source$);
    return response;
  }

  async createRoute(newRoute: IRouteDetails, townStopsIncluded = false, processingOptions?: IProcessingOptions) {
    const source$ = this.routeHttpService.createRoute(newRoute, townStopsIncluded, processingOptions);
    const response = await lastValueFrom(source$);
    const responseRoute = objectToCamel(response as []);
    return responseRoute;
  }

  async createQuickService(payload: any) {
    const source$ = this.routeHttpService.createQuickService(payload);
    const operationId = await lastValueFrom(source$);
    return operationId as string;
  }

  async activate(routeId: number) {
    const source$ = this.routeHttpService.activate(routeId);
    const response = await lastValueFrom(source$);
    const service = objectToCamel(response as object);
    return service;
  }

  async deactivate(routeId: number) {
    const source$ = this.routeHttpService.deactivate(routeId);
    const response = await lastValueFrom(source$);
    const service = objectToCamel(response as object);
    return service;
  }
  
  async deleteRoute(routeId: number) {
    const source$ = this.routeHttpService.deleteRoute(routeId);
    const response = await lastValueFrom(source$);
    return response;
  }

  async getDefaultSetupConfig() {
    const source$ = this.routeHttpService.getDefaultSetupConfig();
    const response = await lastValueFrom(source$);
    return objectToCamel(response as object);
  }

  async createSetupDepartures(routeId: number, setupId: number, departures: any[]) {
    const source$ = this.routeHttpService.createSetupDepartures(routeId, setupId, departures);
    const response = await lastValueFrom(source$);
    return objectToCamel(response as object);
  }

  async getRouteSetups(serviceId: number) {
    const source$ = this.routeHttpService.getRouteSetups(serviceId);
    const response = await lastValueFrom(source$);
    const setups = objectToCamel(response as IRouteSetup[]);
    return setups;
  }

  async getRouteSetupsSchedule(serviceId: number, setupId: number) {
    const source$ = this.routeHttpService.getRouteSetupsSchedule(serviceId, setupId);
    const response = await lastValueFrom(source$);
    const setupDetails = objectToCamel(response as IRouteSetupDetail);
    return setupDetails;
  }

  async activateSetup(id: number, setupId: number) {
    const source$ = this.routeHttpService.activateSetup(id, setupId);
    const response = await lastValueFrom(source$);
    const setupDetails = objectToCamel(response as IRouteSetupDetail);
    return setupDetails;
  }

  async deactivateSetup(id: number, setupId: number) {
    const source$ = this.routeHttpService.deactivateSetup(id, setupId);
    const response = await lastValueFrom(source$);
    const setupDetails = objectToCamel(response as IRouteSetupDetail);
    return setupDetails;
  }

  async updateSetup(routeId: number, setupId: number, routeSetupDetail: IRouteSetupDetail, options?: {
    applyToAvailabilities?: boolean;
    updateTrips?: boolean;
    sendNotification?: boolean;
    deleteOrphanedAvailabilities?: boolean;
    deleteComment?: string;
    refund?: boolean;
    cancelRelated?: boolean;
    fullRefund?: boolean;
  }) {
    const source$ = this.routeHttpService.updateSetup(routeId, setupId, routeSetupDetail, options);
    const response = await lastValueFrom(source$);
    const setupDetails = objectToCamel(response as IRouteSetupDetail);
    return setupDetails;
  }

  async createSetup(routeId: number, routeSetupDetail: IRouteSetupDetail) {
    const source$ = this.routeHttpService.createSetup(routeId, routeSetupDetail);
    const response = await lastValueFrom(source$);
    const setupDetails = objectToCamel(response as IRouteSetupDetail);
    return setupDetails;
  }

  async getTrips(routeId: number, page?: number, size?: number, search?: string) {
    const source$ = this.routeHttpService.getTrips(routeId, page, size, search);
    const response = await lastValueFrom(source$);
    const trips = objectToCamel(response as ITrip);
    const tripsData = this.getData(trips);
    return tripsData;
  }

  async getApps(routeId: number) {
    const source$ = this.routeHttpService.getApps(routeId);
    const response = await lastValueFrom(source$);
    const apps = objectToCamel(response as IApps);
    return apps;
  }

  async getRouteAvailabilities(routeId: number, page?: number, size?: number, search?: string, status?: ExpeditionStatus[], bookings?: boolean, fromDate?: Date, toDate?: Date, startTime?: string, endTime?: string, setupId?: number) {
    const source$ = this.routeHttpService.getRouteAvailabilities(routeId, page, size, search, status, bookings, fromDate, toDate, startTime, endTime, setupId);
    const response = await lastValueFrom(source$);
    const availabilities = objectToCamel(response as object);
    const result = this.getData(availabilities);
    return result;
  }

  async updateAvailabilities(routeId: number, availabilitiesIds: number[], driverId: number, vehicleId: number, updateTrips: boolean,  sendNotification: boolean, time?: any, comment?: string) {
    const source$ = this.routeHttpService.updateAvailabilities(routeId, availabilitiesIds, driverId, vehicleId, updateTrips, sendNotification, time, comment);
    const response = await lastValueFrom(source$);
    const availability = objectToCamel(response as IAvailabilities);
    return availability;
  }

  async createBackupAvailability(routeId: number, availabilityId: number, driverId: number, vehicleId: number, comment?: string) {
    const source$ = this.routeHttpService.createBackupAvailability(routeId, availabilityId, driverId, vehicleId, comment);
    const response = await lastValueFrom(source$);
    const availability = objectToCamel(response as IAvailabilities);
    return availability;
  }

  async deleteAvailabilities(routeId: number, availabilitiesIds: number[], comment: string | undefined, sendNotification: boolean, refund: boolean, cancelRelated: boolean, fullRefund: boolean) {
    const source$ = this.routeHttpService.deleteAvailabilities(routeId, availabilitiesIds, comment, sendNotification, refund, cancelRelated, fullRefund);
    const response = await lastValueFrom(source$);
    const availability = objectToCamel(response as IAvailabilities);
    return availability;
  }

  async getRoutePreview(route: IRouteDetails, rebuildStatus?: string[], townStopsIncluded = false) {
    const source$ = this.routeHttpService.getRoutePreview(route, rebuildStatus, townStopsIncluded);
    const operationId = await lastValueFrom(source$);
    return operationId as string;
  }

  toRoutePreviewChanges(progressEntityData: any): any[] {
    const camelPreview = objectToCamel(progressEntityData ?? {}) as any;
    return (camelPreview.modifiedRoutes ?? []) as any[];
  }


  async cloneSetup(routeId: number, setupId: number, name: string) {
    const source$ = this.routeHttpService.cloneSetup(routeId, setupId, name);
    const response = await lastValueFrom(source$);
    const clonedSetup = objectToCamel(response as IRouteSetupDetail);
    return clonedSetup;
  }

  async getRouteAlterations(routeId: number) {
    const source$ = this.routeHttpService.getRouteAlterations(routeId);
    const response = await lastValueFrom(source$);
    const alterations = objectToCamel(response as object) as any;
    return Array.isArray(alterations) ? alterations.map((alteration) => this.withLocalDateTimes(alteration)) : alterations;
  }

  async getAlteration(routeId: number, alterationId: number) {
    const source$ = this.routeHttpService.getAlteration(routeId, alterationId);
    const response = await lastValueFrom(source$);
    const alteration = objectToCamel(response as IRouteAlteration);
    return this.withLocalDateTimes(alteration);
  }

  // Alteration date times are town wall clock, like availabilities, so they travel zone-less in both directions.
  private withLocalDateTimes(alteration: IRouteAlteration): IRouteAlteration {
    return {
      ...alteration,
      startDateTime: this.utilsService.toLocalDateTimeString(alteration.startDateTime),
      endDateTime: this.utilsService.toLocalDateTimeString(alteration.endDateTime),
    };
  }

  async createAlteration(routeId: number, alteration: IRouteAlteration, processingOptions: IProcessingOptions) {
    const source$ = this.routeHttpService.createAlteration(routeId, this.withLocalDateTimes(alteration), processingOptions);
    const response = await lastValueFrom(source$);
    return response;
  }

  async updateAlteration(routeId: number, alteration: IRouteAlteration, processingOptions: IProcessingOptions) {
    const source$ = this.routeHttpService.updateAlteration(routeId, this.withLocalDateTimes(alteration), processingOptions);
    const response = await lastValueFrom(source$);
    return response;
  }

  async getAlterationPreview(routeId: number, alteration: IRouteAlteration, rebuildStatus?: string[], deleting?: boolean) {
    const source$ = this.routeHttpService.getAlterationPreview(routeId, this.withLocalDateTimes(alteration), rebuildStatus, deleting);
    const operationId = await lastValueFrom(source$);
    return operationId as string;
  }

  async deleteAlteration(routeId: number, alterationId: number, processingOptions: IProcessingOptions) {
    const source$ = this.routeHttpService.deleteAlteration(routeId, alterationId, processingOptions);
    const response = await lastValueFrom(source$);
    return response;
  }
  
  async createRestrictions(routeId: number, restriction: IRestrictions): Promise<IRestrictions[]> {
    const source$ = this.routeHttpService.createRestrictions(routeId, restriction);
    const response = await lastValueFrom(source$);
    return objectToCamel(response as object[]) as IRestrictions[];
  }

  async getRestrictions(routeId: number, page?: number, size?: number, search?: string) {
    const source$ = this.routeHttpService.getRestrictions(routeId, page, size, search);
    const response = await lastValueFrom(source$);
    return objectToCamel(response as object) as any;
  }

  async getRestriction(routeId: number, restrictionId: number) {
    const source$ = this.routeHttpService.getRestriction(routeId, restrictionId);
    const response = await lastValueFrom(source$);
    const converted = objectToCamel(response as object) as IRestrictions & { content?: IRestrictions[] };
    return (converted.content?.[0] ?? converted) as IRestrictions;
  }

  async deleteRestriction(routeId: number, restrictions: IRestrictions[]): Promise<void> {
    const source$ = this.routeHttpService.deleteRestriction(routeId, restrictions);
    await lastValueFrom(source$);
  }

  async changeRestrictionType(routeId: number, type: RestrictionsType, restrictions: IRestrictions[]): Promise<void> {
    const source$ = this.routeHttpService.changeRestrictionType(routeId, type, restrictions);
    await lastValueFrom(source$);
  }

  getData(response: any) {
    return response.data;
  }
}
